import { lazy, useEffect, Suspense } from "react";
import "@/App.css";
import { createBrowserRouter, RouterProvider, Navigate, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { DirtyProvider } from "@/components/SmartLink";
import { PreviewProvider } from "@/components/RecordPreview";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import CalendarPage from "@/pages/CalendarPage";
const Customers = lazy(() => import(/* webpackPrefetch: true */ "@/pages/Customers"));
const CustomerDetail = lazy(() => import(/* webpackPrefetch: true */ "@/pages/CustomerDetail"));
const Inventory = lazy(() => import(/* webpackPrefetch: true */ "@/pages/Inventory"));
const InventoryDetail = lazy(() => import(/* webpackPrefetch: true */ "@/pages/InventoryDetail"));
const Bookings = lazy(() => import(/* webpackPrefetch: true */ "@/pages/Bookings"));
const BookingDetail = lazy(() => import(/* webpackPrefetch: true */ "@/pages/BookingDetail"));
const SettingsPage = lazy(() => import("@/pages/Settings"));
const Appointments = lazy(() => import(/* webpackPrefetch: true */ "@/pages/Appointments"));
const SquareReturn = lazy(() => import("@/pages/SquareReturn"));
const BookPage = lazy(() => import("@/pages/public/BookPage"));
const ManagePage = lazy(() => import("@/pages/public/ManagePage"));
const PrivacyPage = lazy(() => import("@/pages/public/PrivacyPage"));
import { AppointmentDialogProvider } from "@/components/AppointmentDialog";

function Protected() {
  const { user, loading, pinLocked } = useAuth();
  const loc = useLocation();
  useEffect(() => { if (!loading) requestAnimationFrame(() => requestAnimationFrame(() => window.__hideSplash?.())); }, [loading]);
  if (loading) return <div className="min-h-screen bg-white" />;
  if (!user && !pinLocked) return <Navigate to="/login" replace state={{ from: loc.pathname }} />;
  return <PreviewProvider><DirtyProvider><AppointmentDialogProvider><Layout /></AppointmentDialogProvider></DirtyProvider></PreviewProvider>;
}

const router = createBrowserRouter([
  { path: "/login", element: <Login /> },
  { path: "/book", element: <Suspense fallback={<div className="min-h-screen bg-[#FAF6F4]" />}><BookPage /></Suspense> },
  { path: "/book/manage/:token", element: <Suspense fallback={<div className="min-h-screen bg-[#FAF6F4]" />}><ManagePage /></Suspense> },
  { path: "/privacy", element: <Suspense fallback={<div className="min-h-screen bg-[#FAF6F4]" />}><PrivacyPage /></Suspense> },
  {
    element: <Protected />,
    children: [
      { path: "/", element: <Dashboard /> },
      { path: "/customers", element: <Customers /> },
      { path: "/customers/:id", element: <CustomerDetail /> },
      { path: "/inventory", element: <Inventory /> },
      { path: "/inventory/new", element: <InventoryDetail /> },
      { path: "/inventory/:id", element: <InventoryDetail /> },
      { path: "/bookings", element: <Bookings /> },
      { path: "/bookings/new", element: <BookingDetail /> },
      { path: "/bookings/:id", element: <BookingDetail /> },
      { path: "/calendar", element: <CalendarPage /> },
      { path: "/appointments", element: <Appointments /> },
      { path: "/square-return", element: <SquareReturn /> },
      { path: "/reports", element: <Navigate to="/settings/reports" replace /> },
      { path: "/settings", element: <SettingsPage /> },
      { path: "/settings/:tab", element: <SettingsPage /> },
    ],
  },
  { path: "*", element: <Navigate to="/" replace /> },
]);

export default function App() {
  return (
    <AuthProvider>
      <RouterProvider router={router} />
      <Toaster position="top-right" richColors closeButton />
    </AuthProvider>
  );
}
